(ns practitest.frontend.components.markdown
  (:require
    ["react-markdown" :default ReactMarkdown]
    ["remark-gfm" :default remarkGfm]
    ["rehype-raw" :default rehypeRaw]
    ["rehype-sanitize" :default rehypeSanitize :refer [defaultSchema]]
    ["tributejs" :as tributejs]
    [clojure.string :as str]
    [dommy.core :refer [sel1]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.preview :as preview]
    [practitest.frontend.components.toolbar :as toolbar]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]
    [reagent.dom]))

;; TODO: as props ?
(def max-undo-items 50)
(def max-editor-height 300)
(def min-editor-height 150)
(def toolbar-height 36)

;; Use hidden textarea to calculate height to avoid annoying scroll issues
;; inspired by https://github.com/Andarist/react-textarea-autosize/blob/de827c4013dcbaadd3794168e2fed940dc64f09d/src/calculateNodeHeight.ts#L11
(defn- get-hidden-textarea [parent-textarea]
  (let [root-node       (dom/get-root-node parent-textarea)
        hidden-textarea (sel1 root-node "textarea[hidden-textarea=true]")]
    (if hidden-textarea
      hidden-textarea
      (let [node (doto (js/document.createElement "textarea")
                   (.setAttribute "hidden-textarea" "true")
                   (.setAttribute "tabindex" "-1")
                   (.setAttribute "rows" "1")
                   (.setAttribute "aria-hidden" "true")
                   (.setAttribute "class" "form-control m3-memo-hidden-textarea"))]
        ;; Set styles
        (doseq [[k v] {:min-height "0"
                       :max-height "none"
                       :height     "auto"
                       :visibility "hidden"
                       :position   "absolute"
                       :overflow   "hidden"
                       :top        "0"
                       :right      "0"
                       :z-index    "-1000"}]
          (.setProperty (.-style node) (name k) v "important"))

        ;; Attach hidden textarea to shadow DOM/body node
        (.appendChild root-node node)))))

;; Special logic for "empty value" (space is not supposed to be an empty value)
(defn- empty-value?
  [s]
  (or (nil? s)
      (= "" s)))

(defn- calculate-textarea-height [dom-node value]
  (let [hidden-node (get-hidden-textarea dom-node)]
    (set! (.. hidden-node -style -width)
          (str (.-clientWidth dom-node) "px"))
    (set! (.-value hidden-node) value)
    (+ (.-scrollHeight hidden-node)
       ;; This 2 is to prevent scrollbar appearance - seems widespread
       2)))

;; Autogrow/autoshrink for textarea
(defn- update-textarea-height [local-state dom-node max-height min-height fullscreen?]
  (when dom-node
    ;; min-editor-height is to show at least 5 rows in case of blank value
    (let [scroll-height (max min-height
                             (calculate-textarea-height dom-node (.-value dom-node)))
          element-height (if fullscreen?
                           scroll-height
                           (min max-height scroll-height))]
      (swap! local-state assoc :textarea-height element-height)
      (.setProperty (.-style dom-node) "height" (str element-height "px"))
      element-height)))

(defn textarea-autoheight
  [_]
  (let [textarea-ref (atom nil)
        update-height-fn (fn [comp]
                           ;; Different logic from markdown editor
                           (when-let [dom-node @textarea-ref]
                             (let [value (.-value dom-node)
                                   props (reagent/props comp)
                                   min-height (or (:min-height props) 0)
                                   max-height (:max-height props)
                                   scroll-height (max min-height
                                                      (calculate-textarea-height dom-node value))
                                   scroll-height (if (some? max-height)
                                                   (min max-height scroll-height)
                                                   scroll-height)]
                               (.setProperty (.-style dom-node) "height" (str scroll-height "px")))))]
    (reagent/create-class
      {:component-did-mount (fn [component]
                              (update-height-fn component))
       :component-did-update (fn [component]
                               (update-height-fn component))
       :reagent-render (fn [props]
                         [:textarea (assoc props :ref #(reset! textarea-ref %))])})))

(defn- modify-string [s start end value]
  (str (subs s 0 start)
       value
       (subs s end (count s))))

(defn- capture-textarea-state [dom-node]
  {:value           (.-value dom-node)
   :selection-start (.-selectionStart dom-node)
   :selection-end   (.-selectionEnd dom-node)})

(defn- apply-textarea-state! [dom-node state]
  (set! (.-value dom-node) (:value state))
  (when-some [selection-start (:selection-start state)]
    (set! (.-selectionStart dom-node) selection-start))
  (when-some [selection-end (:selection-end state)]
    (set! (.-selectionEnd dom-node) selection-end)))

(defn- conj-limited
  [coll o max-count]
  (vec (take-last max-count (conj coll o))))

(defn- push-undo [state-atom value max-undo]
  (swap! state-atom
         update
         :undo
         conj-limited
         value
         max-undo))

(defn- pop-undo [state-atom redo-state]
  (when-let [new-state (peek (:undo @state-atom))]
    (swap! state-atom
           update
           :undo
           pop)
    (swap! state-atom
           update
           :redo
           conj
           redo-state)
    new-state))

(defn- pop-redo [state-atom]
  (when-let [new-state (peek (:redo @state-atom))]
    (swap! state-atom
           update
           :redo
           pop)
    (swap! state-atom
           update
           :undo
           conj
           new-state)
    new-state))

(defn- update-current-state [state-atom state]
  (swap! state-atom assoc :current-state state))

(defn- surround-text-impl [value selection-start selection-end prefix suffix]
  (let [value            (or value "")

        selected-text    (subs value selection-start selection-end)
        prefix-length    (count prefix)
        suffix-length    (count suffix)

        ;; Check if we already have those suffix and prefix
        lookahead-string (subs value
                               (- selection-start prefix-length)
                               (+ selection-end suffix-length))]

    (if (and (>= (count value)
                 (+ prefix-length suffix-length))
             (str/starts-with? lookahead-string prefix)
             (str/ends-with? lookahead-string suffix))
      ;; Undo effect if already prefixed
      [(modify-string value
                      (- selection-start (count prefix))
                      (+ selection-end (count suffix))
                      selected-text)
       ;; We're removing stuff here, hence the minus
       (- selection-start (count prefix))
       (- selection-end (count suffix))]

      ;; Apply effect if not applied yet
      [(modify-string value
                      selection-start
                      selection-end
                      (str prefix selected-text suffix))
       (+ selection-start (count prefix))
       (+ selection-end (count suffix))])))

(defn- surround-text [textarea-ref local-state-static on-change prefix suffix]
  (when-let [textarea @textarea-ref]
    (let [selection-start (.-selectionStart textarea)
          selection-end   (.-selectionEnd textarea)
          value           (.-value textarea)

          [new-value
           new-selection-start
           new-selection-end] (surround-text-impl value selection-start selection-end prefix suffix)]
      (push-undo local-state-static
                 (capture-textarea-state textarea)
                 max-undo-items)
      (apply-textarea-state! textarea
                             {:value           new-value
                              :selection-start new-selection-start
                              :selection-end   new-selection-end})
      (.focus textarea)
      (on-change new-value))))

(defn- index-backwards
  [s c start]
  (loop [i (dec start)]
    (if (< i 0)
      -1
      (if (= (aget s i) c)
        i
        (recur (dec i))))))

(defn- replace-current-line [s position new-string]
  (when s
    (let [line-start (index-backwards s "\n" position)
          line-start (if (= line-start -1)
                       0
                       (inc line-start))
          line-end   (.indexOf s "\n" position)
          line-end   (if (= -1 line-end)
                       (count s)
                       (inc line-end))]
      (modify-string s line-start line-end new-string))))

(defn- get-current-line
  [text selection-start]
  (when text
    ;; Search for start
    (let [start (index-backwards text "\n" selection-start)
          end   (.indexOf text "\n" selection-start)
          start (if (= -1 start)
                  0
                  (inc start))
          end   (if (= -1 end)
                  (count text)
                  (inc end))]
      (subs text start end))))

(defn- get-previous-line
  [text selection-start]
  (when text
    (if (<= selection-start 0)
      nil
      ;; Start of current line
      (let [start (index-backwards text "\n" selection-start)]
        (cond
          ;; No new line before
          (= start -1)
          nil

          ;; Newline at start of string - that would be empty previous line
          (= start 0)
          "\n"

          ;; Our line would be current line at that point
          :else
          (get-current-line text start))))))

(def ordered-regex #"^\s*(\d+)\.\s")
(def ordered-regex-for-newline #"^\s*(\d+)\.\s.*")

(defn- prepend-each-line [textarea-ref local-state-static on-change value mode]
  (when-let [textarea @textarea-ref]
    (let [selection-start (.-selectionStart textarea)
          selection-end   (.-selectionEnd textarea)
          value           (or value "")
          multiline?      (not= selection-end selection-start)

          previous-line   (get-previous-line value selection-start)
          counter         (atom (if (some? previous-line)
                                  (if-let [re (re-find ordered-regex previous-line)]
                                    ;; Continue counter if we have previous numbered line
                                    ;; fallback to 0 in case of errors
                                    (inc (fu/try-parse-number2 (second re) 0))
                                    1)
                                  1))

          ;; Little complicated here - nested multiple values destructuring
          [new-value new-selection-start new-selection-end]
          (if multiline?
            ;; Multiline selection handling
            (let [selected-text (subs value selection-start selection-end)
                  ;; see https://clojure.atlassian.net/browse/CLJS-3360 about -1 limit
                  lines         (str/split selected-text #"\n" -1)

                  [new-lines new-selection-start new-selection-end]
                  (reduce
                    (fn [[acc-lines start end] s]
                      (let [prefix        (case mode
                                            :unordered "* "
                                            :ordered (str @counter ". ")
                                            :blockquote "> ")

                            ;; Special handling for ordered mode
                            remove?       (if (= :ordered mode)
                                            (some? (re-find ordered-regex s))
                                            (str/starts-with? s prefix))
                            ;; Override prefix for ordered mode in case of removal
                            prefix        (if (and (= :ordered mode)
                                                   remove?)
                                            (first (re-find ordered-regex s))
                                            prefix)

                            prefix-length (count prefix)]

                        (cond
                          ;; Skip empty lines
                          (str/blank? s)
                          [(conj acc-lines s)
                           start
                           end]

                          ;; Remove prefix on second call (if present)
                          remove?
                          [(conj acc-lines (str/replace-first s prefix ""))
                           start
                           (- end prefix-length)]

                          :else
                          (do
                            (swap! counter inc)
                            [(conj acc-lines (str prefix s))
                             start
                             (+ end prefix-length)]))))
                    [[] selection-start selection-end]
                    lines)

                  new-value     (modify-string value
                                               selection-start
                                               selection-end
                                               (str/join "\n" new-lines))]
              ;; Use those values as return values
              [new-value new-selection-start new-selection-end])
            ;; Single line case
            (let [position      selection-start
                  line          (get-current-line value position)
                  prefix        (case mode
                                  :unordered "* "
                                  :ordered (str @counter ". ")
                                  :blockquote "> ")
                  prefix-length (count prefix)]
              ;; Remove on trigger
              (if (str/starts-with? line prefix)
                [(replace-current-line value selection-start (str/replace-first line prefix ""))
                 (- position prefix-length)
                 (- position prefix-length)]

                [(replace-current-line value selection-start (str prefix line))
                 (+ position prefix-length)
                 (+ position prefix-length)])))]

      (push-undo local-state-static
                 (capture-textarea-state textarea)
                 max-undo-items)
      (apply-textarea-state! textarea
                             {:value           new-value
                              :selection-start new-selection-start
                              :selection-end   new-selection-end})
      (.focus textarea)
      (on-change new-value))))

;; This function is very misleading - you can't use it to actually set textarea field value
;; - it will be overwritten on the next update, you need to call set-field-value to actually
;; update text inside textarea field. This function is used to track cursor position and to
;; provide some kind of accumulator for multiple file links insert
(defn insert-text! [textarea-ref text]
  (when-let [textarea @textarea-ref]
    (let [selection-start (.-selectionStart textarea)
          value           (.-value textarea)
          new-value       (modify-string value selection-start selection-start text)]
      (apply-textarea-state! textarea {:value new-value})
      new-value)))

;; Custom sanitation schema for Markdown preview
(def sanitize-schema
  (clj->js {:strip      ["script"]
            :protocols  {:href ["http" "https" "mailto"]
                         :src  ["http" "https"]}
            :tagNames   [;; Standard markdown tags
                         "h1" "h2" "h3" "h4" "h5" "h6"
                         "br" "b" "i" "strong" "em" "a"
                         "blockquote" "code" "hr" "li" "ol" "ul"
                         "pre" "img" "div" "p"
                         ;; GFM
                         "del" "input" "table" "tbody" "td" "th" "thead" "tr"
                         ;; Additional tags
                         "font" "sub" "sup"]
            :attributes {:a     ["href"]
                         :img   ["src"]
                         :font  ["color"]
                         :code  ["class"]
                         :input ["type" "disabled" "checked"]
                         :th    ["style"]
                         :td    ["style"]
                         :ul    ["class"]
                         :li    ["class"]
                         "*"    []}}))

(defn preprocess-newlines [s]
  ;; Replace every newline with two spaces and a newline, which is a markdown line break
  (->> (str s)
       str/split-lines
       (map (fn [line]
              ;; Add "  \n" only if not part of markdown syntax
              (if (re-matches #"^\s*([-*+]|[0-9]+\.)\s+.*" line)
                line
                (str line "  "))))
       (str/join "\n")))

(defn preprocess-nested-lists [s]
  ;; Convert any indented numbered or bulleted list item to a nested bullet
  (-> (str s)
      ;; Replace indented numbered list
      (str/replace #"(?m)^ +\d+\.\s+" "    - ")
      ;; Replace indented bullet
      (str/replace #"(?m)^ +[*+-]\s+" "    - ")))

(defn preprocess-value
  [value]
  (-> value
      preprocess-nested-lists
      preprocess-newlines))

;; I've tried markdown-to-jsx but decided to stick with rehypeRaw + rehypeSanitize
(defn markdown-preview
  ([value] (markdown-preview value nil))
  ([value {:keys [link-target] :or {link-target "_BLANK"}}]
   (let [processed-value (preprocess-value value)]
     [:div.markdown-body
      [:> ReactMarkdown {:remark-plugins [remarkGfm]
                         :rehypePlugins  [rehypeRaw
                                          [rehypeSanitize sanitize-schema]]
                         ;; Override standard image tag with our preview element
                         :components     {:img (fn [args]
                                                 (let [src (.-src args)]
                                                   (reagent/as-element
                                                     [preview/attachment-with-preview src])))

                                          ;; Open links in new window by default
                                          :a (fn [args]
                                               (reagent/create-element
                                                 "a"
                                                 #js {:href (.-href args)
                                                      :target link-target}
                                                 (.-children args)))}}
       processed-value]])))

(defn build-parameters-items
  [{:keys [params on-manage-params]} text]
  (let [param-items  (map (fn [[_ {:keys [name type]}]]
                            #js{:key (str name " (" type ")")
                                :value name})
                          params)
        filtered (filter (fn [itm]
                            (-> itm
                                .-value
                                (.toLowerCase)
                                (.includes (.toLowerCase text))))
                          param-items)
        add-item    (when (and (seq text)
                               (not (.includes text "}}")))
                      #js{:key   (format-translation
                                   (get-translation
                                     "test.step.param.add-new-parameter"
                                     "+ Add new parameter %{text}")
                                   {:text text})
                          :value (str "__add__:" text)})
        manage-item (when on-manage-params
                      #js{:key   (get-translation "test.step.param.manage-parameters" "Manage Parameters")
                          :value "manage-params"})]
    (clj->js (cond-> (vec filtered)
                     add-item    (conj add-item)
                     manage-item (conj manage-item)))))

(defn select-template-fn
  [on-add on-manage item]
  (let [val (.-value (.-original item))]
    (cond
      (= val "manage-params")
      (do (on-manage) "")

      (str/starts-with? val "__add__:")
      (let [param (subs val (count "__add__:"))]
        (on-add param)
        (str "{{" param "}}"))

      :else
      (str "{{" val "}}"))))

(defn init-tribute!
  [node local-state on-add on-manage license users]

  (when-let [old-tribute (:tributejs @local-state)]
    (.detach old-tribute node))

  (let [collections
        (cond-> []
                (and (some? (:params @local-state)) (not= license "Professional"))
                (conj #js{:values         (fn [text cb]
                                            (cb (build-parameters-items @local-state text)))
                          :lookup         "key"
                          :fillAttr       "value"
                          :trigger        "{{"
                          :selectTemplate (fn [item]
                                            (select-template-fn on-add on-manage item))
                          :menuItemTemplate  (fn [item]
                                               (let [orig   (.-original item)
                                                     value  (aget orig "value")
                                                     label  (.-string item)]
                                                 (if (= value "manage-params")
                                                   (str "<span class=\"manage-params-item\">"
                                                        label
                                                        "</span>")
                                                   label)))})
                (some? users)
                (conj #js{:values         (apply array
                                                 (map (fn [user]
                                                        #js{:key   (str (:email user) " - " (:name user))
                                                            :value (:email user)})
                                                      users))
                          :trigger        "@"
                          :selectTemplate (fn [item]
                                            (let [original (.-original item)]
                                              (str "[~" (.-value original) "]")))}))
        tribute (tributejs. (clj->js {:collection collections
                                      :allowSpaces true
                                      :noMatchTemplate ""}))]
    (.attach tribute node)
    (.addEventListener node "tribute-replaced"
                       (fn [e]
                         (when-let [f (:on-tribute-replaced @local-state)]
                           (f e))))
    (swap! local-state assoc :tributejs tribute)))

;; TODO: best way to achieve mentions would be to integrate react-select
;; into overlay (Portal) that would provide consistent UX with filtering
;; portal positioning logic could be quite complex though....
;; see: https://github.com/signavio/react-mentions/blob/79b2c44b4430a76ec8bb16fc94ffc23ae58b9f7c/src/MentionsInput.js#L670
;; for examples of positioning calculations code
;; we're sticking with tribute js for now because it's easier to integrate
(defn tribute-textarea
  [{:keys [params on-add-param on-manage-params on-tribute-replaced license users] :as props}]
  (let [local-state (atom {:params           params
                           :on-add-param     on-add-param
                           :on-manage-params on-manage-params
                           :on-tribute-replaced on-tribute-replaced})
        textarea-ref (atom nil)]
    (reagent/create-class
      {:component-did-mount (fn [_]
                              (let [node @textarea-ref]
                                (init-tribute! node local-state on-add-param on-manage-params license users)
                                (.addEventListener node "input"
                                                   (fn [^js e]
                                                     (let [text (.-value node)
                                                           match (some->> text
                                                                          (re-find #"\{\{([^\{\}]+)\}\}$")
                                                                          second
                                                                          str/trim)]
                                                       (when (and on-add-param
                                                                  match
                                                                  (not (contains? (:params @local-state) (keyword match))))
                                                         (on-add-param match)))))))

       :component-did-update (fn [this [_ old-props]]
                               (let [[_ new-props] (reagent/argv this)
                                     new-params    (:params new-props)
                                     old-params    (:params old-props)
                                     new-users     (:users new-props)
                                     old-users     (:users old-props)
                                     tribute-init  #(init-tribute! @textarea-ref
                                                                  local-state
                                                                  (:on-add-param new-props)
                                                                  (:on-manage-params new-props)
                                                                  (:license new-props)
                                                                  new-users)]

                                 (when (not= old-params new-params)
                                   (swap! local-state assoc
                                          :params           new-params
                                          :on-add-param     (:on-add-param new-props)
                                          :on-manage-params (:on-manage-params new-props))
                                   (tribute-init))

                                 (when (not= old-users new-users)
                                   (tribute-init))))

       :reagent-render (fn [props]
                         (let [textarea-props (-> props
                                                  (dissoc :params :on-add-param :on-manage-params :on-tribute-replaced :license :users)
                                                  (assoc :ref (fn [node]
                                                                (reset! textarea-ref node)
                                                                (when-let [ref (:ref props)]
                                                                  ((dom/ref-handler ref) node)))))]
                           [:textarea textarea-props]))})))

;; TODO:
; 2. Move caret back where it was on preview toggle
; 3. Ideally: preview should be scrolled to the same item as in the textarea
; 5. Highlight formatting buttons if selected text matches formatting
(defn markdown-editor [props]
  (let [local-state-updating (reagent/atom {:textarea-focused? false
                                            :fullscreen? false})
        local-state-static (atom {:undo []
                                  :redo []})
        local-textarea-ref (atom nil)

        update-height-fn (fn [comp]
                           (let [focused? (:textarea-focused? @local-state-updating)
                                 {:keys [autosize? external-fullscreen? min-height] :or {autosize? true}} (reagent/props comp)
                                 fullscreen? (or external-fullscreen? (:fullscreen? @local-state-updating))
                                 effective-min-height (or min-height min-editor-height)]
                             (when autosize?
                               (update-textarea-height local-state-static
                                                       @local-textarea-ref
                                                       max-editor-height
                                                       (if focused?
                                                         (- effective-min-height toolbar-height)
                                                         effective-min-height)
                                                       fullscreen?))))]
    (reagent/create-class
      {:display-name "markdown-editor"
       :component-did-update (fn [comp [_ old-props]]
                               (update-height-fn comp)
                               ;; When external fullscreen activates, switch to edit mode
                               (let [{old-fullscreen? :external-fullscreen?} old-props
                                     {new-fullscreen? :external-fullscreen?} (reagent/props comp)]
                                 (when (and (not old-fullscreen?) new-fullscreen?)
                                   (swap! local-state-updating assoc :textarea-focused? true))))
       :component-did-mount (fn [comp]
                              (update-height-fn comp)
                              (let [[this options] (reagent/argv comp)
                                    textarea @local-textarea-ref]
                                ;; save initial state on component mount
                                (update-current-state local-state-static
                                                      (if textarea
                                                        (capture-textarea-state textarea)
                                                        {:value (:value options)}))))

       :reagent-render
       (fn [{:keys [value

                    id
                    class
                    preview-class

                    placeholder

                    ;; events
                    on-change
                    on-load-file

                    ;; textarea ref support
                    textarea-ref

                    read-only?
                    hide?

                    on-drag-over
                    on-drag-leave
                    on-drop
                    on-focus
                    on-blur

                    footer-content
                    external-fullscreen?
                    on-fullscreen-exit
                    fullscreen-backdrop?]
             :as props}]

         (let [{:keys [side-by-side?
                       fullscreen?
                       preview?
                       textarea-focused?]} @local-state-updating

               fullscreen? (or external-fullscreen? fullscreen?)

               enable-preview! (fn [e]
                                 (swap! local-state-updating assoc
                                        :textarea-focused? false))

               exit-fullscreen! (fn []
                                  (if (and external-fullscreen? on-fullscreen-exit)
                                    (on-fullscreen-exit)
                                    (swap! local-state-updating assoc
                                           :fullscreen? false
                                           :textarea-focused? false)))

               preview? (and (or preview?
                                 (not textarea-focused?))
                             (not (empty-value? value)))
               project-id @(re-frame/subscribe [::subs/current-project-id])
               license @(re-frame/subscribe [::subs/account-license])]

           (if read-only?
             ;; Readonly mode - preview with optional footer
             [:div.m3-memo-preview
              {:class preview-class}
              [markdown-preview value]
              (when footer-content
                [:div.m3-memo-footer
                 footer-content])]
             ;; Editable mode - switchable between preview and text editor
             [:<>
              ;; Clickable backdrop wrapper for fullscreen mode
              (when (and fullscreen? fullscreen-backdrop?)
                [:div.m3-memo-fullscreen-backdrop
                 {:on-click exit-fullscreen!}])

              [:div.m3-memo-field
              {:class (dom/classes-pairs :focused (or textarea-focused?)
                                         :fullscreen fullscreen?
                                         class class)
               :on-click (fn [e]
                           (swap! local-state-updating assoc :textarea-focused? true)
                           ;; TODO: this delayed focus call causes issues when clicking fast between input fields
                           ;; Either think of a way to do immmediate focus or add "click outside" handler to remove focused effect
                           (dom/focus-on-ref! local-textarea-ref false))
               :on-drag-over (fn [e]
                               (when on-drag-over
                                 (on-drag-over e)))
               :on-drag-leave (fn [e]
                                (when on-drag-leave
                                  (on-drag-leave e)))
               :on-drop (fn [e]
                          (when on-drop
                            (on-drop e)))
               :on-focus (fn [e]
                           (when on-focus
                             (on-focus e)))}

              ;; Will be rendered in case of empty-value? - so need to hide it until textarea is focused
              (when textarea-focused?
                (let [surround-text-fn (fn [prefix suffix]
                                         (surround-text local-textarea-ref local-state-static on-change prefix suffix))
                      prepend-each-line-fn (fn [mode]
                                             (prepend-each-line local-textarea-ref local-state-static on-change value mode))

                      formatting-buttons
                      [[toolbar/base-toolbar-button
                        {:title (get-translation "markdown.toolbar.bold-text" "Bold text")
                         :on-click (fn [e]
                                     (surround-text-fn "**" "**"))
                         :icon [icons/mux-text-bold]}]
                       [toolbar/base-toolbar-button
                        {:title (get-translation "markdown.toolbar.italic-text" "Italic text")
                         :on-click (fn [e]
                                     (surround-text-fn "_" "_"))
                         :icon [icons/mux-text-italic]}]
                       [:div.m3-memo-toolbar-separator]
                       [toolbar/base-toolbar-button
                        {:title (get-translation "markdown.toolbar.unordered-list" "Unordered list")
                         :on-click (fn [e]
                                     (prepend-each-line-fn :unordered))
                         :icon [icons/mux-unordered-list]}]
                       [toolbar/base-toolbar-button
                        {:title (get-translation "markdown.toolbar.ordered-list" "Ordered list")
                         :on-click (fn [e]
                                     (prepend-each-line-fn :ordered))
                         :icon [icons/mux-ordered-list]}]
                       [toolbar/base-toolbar-button
                        {:title (get-translation "markdown.toolbar.blockquote" "Blockquote")
                         :on-click (fn [e]
                                     (prepend-each-line-fn :blockquote))
                         :icon [icons/mux-blockquote]}]
                       [toolbar/base-toolbar-button
                        {:title (get-translation "markdown.toolbar.code-block" "Code block")
                         :on-click (fn [e] (surround-text-fn "```\n" "\n```"))
                         :icon [icons/mux-codeblock]}]]

                      all-buttons (if (:params props)
                                    (conj formatting-buttons
                                          [toolbar/base-toolbar-button
                                           {:title (if (= license "Professional")
                                                     (get-translation "test.step.param.disable.tooltip"
                                                                      "Step Parameters aren't available on your current plan. Please contact your administrator")
                                                     (get-translation "markdown.toolbar.add-params" "Add Parameter While Typing"))
                                            :dropdown-text (get-translation "markdown.toolbar.add-params-basic" "Add Parameter")
                                            :disabled? (= license "Professional")
                                            :on-click
                                            (fn [e]
                                              (dom/prevent-default e)
                                              (mixpanel/track "Click on Parameter Icon")
                                              (when-let [ta @local-textarea-ref]
                                                (insert-text! local-textarea-ref "{{")
                                                ;; Fire synthetic events to trigger tribute
                                                (doseq [phase ["keydown" "keypress" "keyup"]]
                                                  (let [evt (js/KeyboardEvent. phase
                                                                               #js {:key "{"
                                                                                    :code "BracketLeft"
                                                                                    :bubbles true})]
                                                    (.dispatchEvent ta evt)))))
                                            :icon [icons/mux-add-param]}])
                                    formatting-buttons)]

                  [:div.m3-memo-toolbar
                   ;; Prevent toolbar from grabbing focus
                   {:on-mouse-down dom/prevent-default}

                   ;; Regular toolbar buttons
                   (into [:div.m3-toolbar-regular-buttons]
                         all-buttons)

                   ;; Small screens toolbar buttons
                   [toolbar/toolbar-overflow-menu all-buttons]

                   [:div.m3-memo-toolbar-separator.pull-right]

                   ;; Preview
                   [toolbar/base-toolbar-button
                    {:class (dom/classes-pairs-v :active (:preview? @local-state-updating))
                     :title (get-translation "markdown.toolbar.preview" "Preview")
                     :on-click (fn [e]
                                 (enable-preview! e)
                                 ;; Will be propagated to parent div and then will force focusing otherwise
                                 (dom/stop-propagation e)
                                 (.blur @local-textarea-ref))
                     :icon [icons/mux-preview]}]

                   [toolbar/base-toolbar-button
                      {:title (get-translation "markdown.toolbar.side-by-side" "Toggle Side by Side")
                       :class (dom/classes-pairs-v :active (:side-by-side? @local-state-updating))
                       :on-click (fn [_]
                                   (swap! local-state-updating
                                          (fn [o]
                                            (-> o
                                                (assoc :preview? false)
                                                (update :side-by-side? not)))))
                       :icon [icons/mux-split-screen]}]

                   (when-not hide?
                     [toolbar/base-toolbar-button
                      {:title (if fullscreen?
                                (get-translation "grid.button.collapse" "Collapse")
                                (get-translation "grid.button.expand" "Expand"))
                       :class (dom/classes-pairs-v :active fullscreen?)
                       :on-click (fn [e]
                                   (if (and external-fullscreen? fullscreen? on-fullscreen-exit)
                                       (on-fullscreen-exit)
                                       (swap! local-state-updating (fn [o]
                                                                     (update o :fullscreen? not)))))
                       :icon (if fullscreen?
                               [icons/mux-collapse]
                               [icons/mux-fullscreen])}])]))

              (when (and preview?
                         (not side-by-side?))
                ;; Preview inside editor
                [:div.m3-memo-preview
                 {:class preview-class}
                 [markdown-preview value]])

              ;; Editor mode - container with side-by-side support
              [:div.m3-memo-textarea-container
               {:class (dom/classes-pairs-v :side-by-side side-by-side?
                                            ;; We don't want to umount text editor on preview
                                            ;; otherwise current position is lost
                                            :hidden preview?)}
               ;; Main editor
               [tribute-textarea
                {:value (or value "")
                 ;; This one is required to prevent scroll from twitching on preview toggle
                 :style (when-let [height (:textarea-height @local-state-static)]
                          {:height (str height "px")})
                 :class (dom/classes-pairs :fullscreen fullscreen?
                                           :sided side-by-side?
                                           :m3-memo-textarea true)
                 :on-mouse-down (fn [e]
                                  (update-current-state local-state-static
                                                        (capture-textarea-state (.-target e))))
                 :on-mouse-up (fn [e]
                                (update-current-state local-state-static
                                                      (capture-textarea-state (.-target e))))

                 :placeholder placeholder

                 :id id

                 :ref (fn [node]
                        (reset! local-textarea-ref node)
                        (when textarea-ref
                          ((dom/ref-handler textarea-ref) node)))

                 :on-key-down (fn [e]
                                ;(js/console.log e)

                                (let [key-code (.-code e)
                                      meta-key? (.-metaKey e)
                                      ctrl-key? (.-ctrlKey e)
                                      textarea (.-target e)]
                                  (cond
                                    ;; Special case for continuing current formatting
                                    (and (not meta-key?)
                                         (not ctrl-key?)
                                         ;; Not using key-code here, because Enter and NumpadEnter are different
                                         (= "Enter" (.-key e)))
                                    (let [value (.-value textarea)
                                          selection-start (.-selectionStart textarea)
                                          selection-end (.-selectionEnd textarea)
                                          line (get-current-line value selection-start)
                                          ordered-check (re-find ordered-regex-for-newline line)
                                          other-check (re-find #"^([*>] ).+" line)]

                                      (cond
                                        ;; Special ordered list case
                                        (some? ordered-check)
                                        (let [num (fu/try-parse-number2 (second ordered-check) 0)
                                              prefix (str "\n" (inc num) ". ")
                                              new-position (+ selection-start (count prefix))
                                              new-value (modify-string value selection-start selection-end prefix)]
                                          (dom/prevent-default e)
                                          (push-undo local-state-static
                                                     (capture-textarea-state textarea)
                                                     max-undo-items)
                                          (apply-textarea-state! (.-target e)
                                                                 {:value new-value
                                                                  :selection-start new-position
                                                                  :selection-end new-position})
                                          (on-change new-value))

                                        ;; Unordered and blockquote cases
                                        (some? other-check)
                                        (let [prefix (str "\n" (second other-check))
                                              new-position (+ selection-start (count prefix))
                                              new-value (modify-string value selection-start selection-end prefix)]
                                          (dom/prevent-default e)
                                          (push-undo local-state-static
                                                     (capture-textarea-state textarea)
                                                     max-undo-items)
                                          (apply-textarea-state! (.-target e)
                                                                 {:value new-value
                                                                  :selection-start new-position
                                                                  :selection-end new-position})
                                          (on-change new-value))))

                                    ;; Undo
                                    (and (or meta-key?
                                             ctrl-key?)
                                         (= "KeyZ" key-code))
                                    (do
                                      (when-let [new-state (pop-undo local-state-static
                                                                     (capture-textarea-state textarea))]
                                        (apply-textarea-state! textarea new-state)
                                        (on-change (:value new-state)))
                                      (dom/prevent-default e))

                                    ;; Redo
                                    (and (or meta-key?
                                             ctrl-key?)
                                         (= "KeyY" key-code))
                                    (do
                                      (when-let [new-state (pop-redo local-state-static)]
                                        (apply-textarea-state! textarea new-state)
                                        (on-change (:value new-state)))
                                      (dom/prevent-default e))

                                    ;; Bold
                                    (and (or meta-key?
                                             ctrl-key?)
                                         (= "KeyB" key-code))
                                    (do
                                      (surround-text local-textarea-ref local-state-static on-change "**" "**")
                                      (dom/prevent-default e))

                                    ;; Italic
                                    (and (or meta-key?
                                             ctrl-key?)
                                         (= "KeyI" key-code))
                                    (do
                                      (surround-text local-textarea-ref local-state-static on-change "_" "_")
                                      (dom/prevent-default e))

                                    :else
                                    (update-current-state local-state-static
                                                          (capture-textarea-state textarea)))))

                 :on-tribute-replaced (fn [e]
                                        ;; Do not save undo history here
                                        (update-current-state local-state-static (capture-textarea-state (.-target e)))
                                        (when on-change
                                          (on-change (dom/event-value e))))

                 :on-change (fn [e]
                              (push-undo local-state-static
                                         (:current-state @local-state-static)
                                         max-undo-items)
                              (update-current-state local-state-static (capture-textarea-state (.-target e)))

                              (when on-change
                                (on-change (dom/event-value e))))

                 :on-focus (fn [e]
                             (swap! local-state-updating
                                    assoc
                                    :textarea-focused? true))
                 :on-blur (fn [e]
                            ;; Check that focus has actually left a textarea
                            ;; on-blur fired on e.g. Alt+Tab event when focus is actually still inside
                            ;; In fullscreen mode, don't exit fullscreen on blur - let click-outside handle it
                            (when (and (not= (.-target e) (.-activeElement js/document))
                                       (not fullscreen?))
                              (enable-preview! e))

                            (when on-blur
                              (on-blur e)))
                 :on-paste (fn [e]
                             (when-let [data (and on-load-file (.-clipboardData e))]
                               (let [files (array-seq (.. data -files))]
                                 (when (> (count files) 0)
                                   (dom/prevent-default e)
                                   (doseq [file files :when file]
                                     (on-load-file file))))))

                 :users (:users props)
                 :params (:params props)
                 :on-add-param (:on-add-param props)
                 :on-manage-params (fn []
                                     (mixpanel/track "Go to Manage Parameters")
                                     (dom/open-in-new-tab!
                                        (broutes/get-url-view "projects#step_params" {:project_id project-id})))
                 :license license}]

               (when side-by-side?
                 [:<>
                  [:div.side-by-side-splitter]
                  [:div.m3-memo-preview
                   {:style {:height (when fullscreen?
                                      "100%")}}
                   (markdown-preview value)]])]
              (when footer-content
                [:div.m3-memo-footer
                 {:on-click (fn [e]
                              (dom/stop-propagation e))
                  :on-mouse-down (fn [e]
                                   (dom/prevent-default e)
                                   (dom/stop-propagation e))}
                 footer-content])]])))})))
